// Standard imports
import React from "react";
import { endpoints } from "config";
import { useLocation } from "react-router-dom";
import DeleteModal from "components/helpers/DeleteModal";
import Category from "components/Category";
import { CrudItems } from "components/Category/CrudMenu";
import { CardListPage } from "components/Category/Category";
import { parseListDate } from "helpers/parseListDate";
import { lazyWithRetry } from "helpers/lazyWithRetry";
// Icons
import Delete from "assets/Icon_Action_03.png";
import Update from "assets/Icon_Action_02.png";
import View from "assets/Icon_Action_01.png";
// CommunicationCategoryCustomType CRUD page
const CommunicationCategoryCustomType = lazyWithRetry(() => import("./CommunicationCategoryCustomType"));

enum Crud {
  CREATE = "CREATE",
  UPDATE = "UPDATE",
}

export type Data = {
  communicationCategoryTypes__Id: number;
  communicationCategoryTypes__Name: string;
  communicationTypes__Name: string;
  communicationCategoryTypes__Description: string;
  communicationCategoryTypes__OrderNumber: number;
  communicationCategoryTypes__bActive: boolean;
  communicationCategoryTypes__AddDate: string;
  communicationCategoryTypes__AddedBy: string;
  communicationCategoryTypes__EditDate: string;
  communicationCategoryTypes__LastEditedBy: string;
};

export const idColumn = "communicationCategoryTypes__Id";

const url = endpoints.communicationCategoriesCustomTypes.list;

const crudItems: CrudItems<Data> = [
  {
    icon: View,
    imgAltText: "Create",
    permissionsKey: "create",
    title: "Create Communication Category",
    menuCode: "CREATE",
    showInMultiSelect: false,
    content: (onDone, selectedData, menu) => (
      <CommunicationCategoryCustomType
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
        actionType="create"
        submitButtonText="Add"
      />
    ),
    inRightPanel: true,
    isDisabled: false,
    notInMenus: true,
  },
  {
    icon: Update,
    imgAltText: "Update",
    permissionsKey: "edit",
    title: "Update Communication Category",
    menuCode: "UPDATE",
    showInMultiSelect: false,
    content: (onDone, selectedData, menu) => (
      <CommunicationCategoryCustomType
        selectedData={selectedData}
        onDone={onDone}
        idFromRowOption={menu.selectedId}
        actionType="edit"
        submitButtonText="Update"
      />
    ),
    inRightPanel: true,
    isDisabled: false,
  },
  {
    icon: Delete,
    imgAltText: "Delete",
    permissionsKey: "delete",
    title: data =>
      data.length <= 1
        ? "Delete Communication Category"
        : `Delete ${data.length} Communication Categories`,
    menuCode: "DELETE",
    showInMultiSelect: true,
    content: (onActionComplete, selectedData, menu) => (
      <DeleteModal
        url={endpoints.communicationCategoriesCustomTypes.delete}
        noun="Communication Category"
        selectedIds={selectedData.map(d => d.communicationCategoryTypes__Id)}
        renderListItem={data => {
          return (
            <li>{`${data.communicationCategoryTypes__Id}: ${data.communicationCategoryTypes__Name}`}</li>
          );
        }}
        selectedData={selectedData}
        open={menu.currentMenu === "DELETE"}
        close={onActionComplete}
        idFromRowOption={menu.selectedId}
      />
    ),
    inRightPanel: false,
    isDisabled: false,
  },
];

const rowFormatter = (
    row: Data
  ): { [K in keyof typeof row]: React.ReactNode } => {
    let formattedRow: any = { ...row };

    if (formattedRow.communicationCategoryTypes__bActive) {
      formattedRow = {
        ...formattedRow,
        communicationCategoryTypes__bActive: "active",
      };
    }

    if (formattedRow.communicationCategoryTypes__AddDate) {
      formattedRow = {
        ...formattedRow,
        communicationCategoryTypes__AddDate: parseListDate(
          formattedRow.communicationCategoryTypes__AddDate,
          true
        ),
      };
    }
  
    if (formattedRow.communicationCategoryTypes__EditDate) {
      formattedRow = {
        ...formattedRow,
        communicationCategoryTypes__EditDate: parseListDate(
          formattedRow.communicationCategoryTypes__EditDate,
          true
        ),
      };
    }
  
    return formattedRow;
  };

  export type Props = {};

  const CommunicationCategoryCustomTypes: React.FC = () => {
    const location = useLocation();
    return (
      <div className="CommunicationCategoryCustomTypesPage CardListPage">
        <Category<Data>
          location={location}
          noun="Communication Category"
          idColumn={idColumn}
          listEndpointUrl={url}
          defaultBActiveFilterColumnKey={"communicationCategoryTypes__bActive"}
          rowFormatter={rowFormatter}
          hiddenColumns={[idColumn]}
          crudComponents={crudItems}
          hideHeaderTitle={true}
          defaultCardOrListView={CardListPage.Card}
          largeRightPanel={false}
          hideCardPage={true}
          rowCellOptions={{
            communicationCategoryTypes__Name: { width: "Large2" },
            communicationTypes__Name: { width: "Large2" },
            communicationCategoryTypes__Description: { width: "Huge3" },
            communicationCategoryTypes__OrderNumber: { width: "Huge" },
            communicationCategoryTypes__bActive: { width: "Medium" },
            communicationCategoryTypes__AddDate: { width: "Medium2" },
            communicationCategoryTypes__AddedBy: { width: "SemiLarge2" },
            communicationCategoryTypes__EditDate: { width: "Medium2" },
            communicationCategoryTypes__LastEditedBy: { width: "SemiLarge2" },
          }}
        />
      </div>
    );
  };
  

export default CommunicationCategoryCustomTypes;